import type { Option, Screen } from '../types';

export type WorkflowOptionValue = Exclude<Option, null>;

export interface WorkflowOptionMeta {
  value: WorkflowOptionValue;
  shortLabel: string;
  summary: string;
  setupNote: string;
  emphasis: string;
  deliverables: readonly string[];
}

export interface WorkflowStepMeta {
  screen: Screen;
  label: string;
  description: string;
}

export const workflowOptions: readonly WorkflowOptionMeta[] = [
  {
    value: 'Iso Drawings Generation',
    shortLabel: 'ISO drawings',
    summary: 'Map Revit piping data to the PCF fields used to generate isometric drawings.',
    setupNote: 'Configures connector data, ISO category tags, and the PCF mapping profile used at export.',
    emphasis: 'Choose this when PCF output will drive the ISO drawing deliverable.',
    deliverables: ['PCF mapping', 'ISO export setup', 'Category tagging'],
  },
  {
    value: 'Pressure Drop Calculations',
    shortLabel: 'Pressure drop',
    summary: 'Map Revit piping data for downstream pressure drop calculations.',
    setupNote: 'Configures analysis parameters, connector data, and consistent system grouping.',
    emphasis: 'Choose this when the deliverable is calculation-ready model data rather than an ISO drawing.',
    deliverables: ['Analysis parameters', 'Connector defaults', 'System grouping'],
  },
  {
    value: 'Stress Calculations',
    shortLabel: 'Stress analysis',
    summary: 'Map Revit piping data for downstream stress calculations.',
    setupNote: 'Configures component identity, stress data, and a validated mapping handoff.',
    emphasis: 'Choose this when the deliverable is stress-ready model data without ISO-only tags.',
    deliverables: ['Stress-ready mapping', 'Quick Fill review', 'Validation handoff'],
  },
] as const;

export const workflowOptionLookup: Record<WorkflowOptionValue, WorkflowOptionMeta> = {
  'Iso Drawings Generation': workflowOptions[0],
  'Pressure Drop Calculations': workflowOptions[1],
  'Stress Calculations': workflowOptions[2],
};

export const screenMeta: Record<Screen, WorkflowStepMeta> = {
  welcome: {
    screen: 'welcome',
    label: 'Overview',
    description: 'See what PipeEX will add or map in Revit before setup begins.',
  },
  menu: {
    screen: 'menu',
    label: 'Workflow',
    description: 'Choose the deliverable, Revit parameter source, and baseline PCF mappings.',
  },
  connectorBinding: {
    screen: 'connectorBinding',
    label: 'Connectors',
    description: 'Map Revit parameters to the connector, BOM, and component fields written to PCF.',
  },
  quickFillProperties: {
    screen: 'quickFillProperties',
    label: 'Quick Fill',
    description: 'Fill and review the family values that downstream mapping and export will read.',
  },
  boltConnection: {
    screen: 'boltConnection',
    label: 'Bolts',
    description: 'Choose the PCF bolt structure and map the Revit parameters that supply it.',
  },
  additionalCategories: {
    screen: 'additionalCategories',
    label: 'Categories',
    description: 'Map Revit valve, support, and instrument identifiers to ISO output.',
  },
  processProperties: {
    screen: 'processProperties',
    label: 'Properties',
    description: 'Map process and project metadata from Revit into the PCF profile.',
  },
  mappingConfiguration: {
    screen: 'mappingConfiguration',
    label: 'Mapping',
    description: 'Review the source-to-PCF rules, resolve gaps, and save the mapping profile.',
  },
  pcfValidation: {
    screen: 'pcfValidation',
    label: 'Validation',
    description: 'Resolve source-data and mapping issues before generating PCF output.',
  },
};

const baseWorkflowScreens: readonly Screen[] = [
  'welcome',
  'menu',
  'connectorBinding',
  'quickFillProperties',
  'processProperties',
  'mappingConfiguration',
];

export const workflowIncludesIsoOnlySetup = (selectedOption: string | null | undefined): boolean =>
  selectedOption == null || selectedOption === 'Iso Drawings Generation';

export const getWorkflowSteps = (
  selectedOption: Option,
  currentScreen?: Screen
): WorkflowStepMeta[] => {
  const includeAdditionalCategories = selectedOption === 'Iso Drawings Generation';
  const screens: Screen[] = includeAdditionalCategories
    ? [
        'welcome',
        'menu',
        'connectorBinding',
        'quickFillProperties',
        'boltConnection',
        'additionalCategories',
        'processProperties',
        'mappingConfiguration',
      ]
    : workflowIncludesIsoOnlySetup(selectedOption)
      ? [...baseWorkflowScreens.slice(0, 4), 'boltConnection', ...baseWorkflowScreens.slice(4)]
      : [...baseWorkflowScreens];

  if (currentScreen === 'pcfValidation') {
    screens.push('pcfValidation');
  }

  return screens.map((screen) => screenMeta[screen]);
};

export const getWorkflowProgress = (screen: Screen, selectedOption: Option) => {
  const steps = getWorkflowSteps(selectedOption, screen);
  const rawActiveIndex = steps.findIndex((step) => step.screen === screen);
  const activeIndex = rawActiveIndex === -1 ? 0 : rawActiveIndex;
  const currentStep = steps[activeIndex] ?? screenMeta[screen];
  const progressValue =
    steps.length === 0 ? 0 : ((activeIndex + 1) / steps.length) * 100;

  return {
    steps,
    activeIndex,
    currentStep,
    progressValue,
  };
};

export const getNextWorkflowScreen = (
  screen: Screen,
  selectedOption: Option
): Screen | null => {
  switch (screen) {
    case 'welcome':
      return 'menu';
    case 'menu':
      return 'connectorBinding';
    case 'connectorBinding':
      return 'quickFillProperties';
    case 'quickFillProperties':
      return workflowIncludesIsoOnlySetup(selectedOption)
        ? 'boltConnection'
        : 'processProperties';
    case 'boltConnection':
      return selectedOption === 'Iso Drawings Generation'
        ? 'additionalCategories'
        : 'processProperties';
    case 'additionalCategories':
      return selectedOption === 'Iso Drawings Generation'
        ? 'processProperties'
        : 'mappingConfiguration';
    case 'processProperties':
      return 'mappingConfiguration';
    default:
      return null;
  }
};

export const getContinueToNextWorkflowStepText = (
  screen: Screen,
  selectedOption: Option
): string => {
  const nextScreen = getNextWorkflowScreen(screen, selectedOption);
  if (!nextScreen) {
    return 'Continue';
  }

  return `Continue to ${screenMeta[nextScreen].label}`;
};
